// ── Generation API client ────────────────────────────────────────
// Thin wrapper around the Digital Products API Generation module
// (see http://localhost:8400 → Generation).

import { request, requestWithMeta } from "@/lib/api/client";
import {
  cachedRequest,
  invalidateGenerationsAndProjects,
  GENERATIONS_CACHE_PREFIX,
  LIST_TTL_MS,
} from "@/lib/api/cache";
import type { ApiListMeta } from "@/lib/api/projects";
import type { ProjectItem, ToolRunArtifact, ToolRunModality } from "@/lib/types";

const TOOL_RUN_MODALITIES: readonly ToolRunModality[] = [
  "text",
  "image",
  "speech",
];

/** Narrow an API modality string to the frontend union (or `undefined`). */
export function castToolRunModality(value: unknown): ToolRunModality | undefined {
  return typeof value === "string" &&
    (TOOL_RUN_MODALITIES as readonly string[]).includes(value)
    ? (value as ToolRunModality)
    : undefined;
}

export interface ApiGenerationHistoryProject {
  slug: string;
  name: string;
  description?: string | null;
}

export interface ApiGenerationHistoryArtifact {
  artifactId: string;
  role: "preview" | "download" | "stream";
  url: string;
  fileName: string;
  mimeType: string;
  extension: string;
}

/**
 * One row from GET /v1/generation / GET /v1/generation/{slug}.
 * Public history is slim: no `toolSlug` / `locale` / `artifacts` today.
 */
export interface ApiGenerationHistoryItem {
  id: number;
  slug: string;
  /**
   * User-provided generation title from the run request (`title` in the API).
   * Null for legacy rows — UI falls back to toolTitle / slug.
   */
  title?: string | null;
  toolSlug?: string;
  toolTitle: string;
  locale?: string;
  modality: string;
  resultMarkdown?: string | null;
  project?: ApiGenerationHistoryProject | null;
  artifacts?: ApiGenerationHistoryArtifact[];
  createdAt: string;
}

export interface FetchGenerationsParams {
  page?: number;
  perPage?: number;
  /** Filter history rows linked to the given project slug. */
  projectSlug?: string;
  /** Preferred locale for toolTitle resolution (`Accept-Language`). */
  locale?: string;
}

export interface FetchGenerationParams {
  /** Preferred locale for toolTitle resolution (`Accept-Language`). */
  locale?: string;
}

/** Body for PATCH /v1/generation/{slug} (`GenerationUpdateRequest`). */
export interface GenerationUpdateBody {
  /** User-facing generation title (max 255). */
  title: string;
}

/** Fallback display title from the public history slug when `title` is null. */
export function generationDisplayName(slug: string): string {
  const text = slug.replace(/-/g, " ").trim();
  if (!text) return slug;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Prefer API `title`; when missing/empty, use `toolTitle` / slug. */
export function generationItemTitle(item: {
  title?: string | null;
  toolTitle: string;
  slug: string;
}): string {
  return item.title?.trim() || item.toolTitle?.trim() || item.slug;
}

/** Capitalize modality for list UI (e.g. "text" → "Text"). */
export function modalityDisplayLabel(modality: string): string {
  const value = modality.trim();
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * History list row copy.
 * Title is API `title` when present, else toolTitle / slug fallback.
 * Subtitle is project name, then tool title. Modality is rendered separately as a badge.
 */
export function historyItemListLabels(item: ApiGenerationHistoryItem): {
  title: string;
  subtitle: string;
  modality: string;
} {
  const subtitle = [item.project?.name, item.toolTitle]
    .filter(Boolean)
    .join(" · ");
  return {
    title: generationItemTitle(item),
    subtitle,
    modality: modalityDisplayLabel(item.modality),
  };
}

/** Short list date, e.g. "Jul 19". */
export function formatGenerationDate(createdAt: string, locale?: string): string {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale || undefined, {
    month: "short",
    day: "numeric",
  });
}

/** True when `createdAt` falls on the local calendar day. */
export function isGenerationToday(createdAt: string): boolean {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

/**
 * GET /v1/generation — paginated history for the current user.
 *
 * Cached per page/perPage/projectSlug/locale: this backs both the /history
 * screen and the sidebar Recents list, which refetched on every open of the
 * drawer. Runs and renames invalidate the namespace at the write site, so a
 * new generation is never missing from the list.
 */
export function fetchGenerations(
  params: FetchGenerationsParams = {},
): Promise<{ data: ApiGenerationHistoryItem[]; meta: ApiListMeta }> {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set("page", String(params.page));
  if (params.perPage != null) qs.set("perPage", String(params.perPage));
  if (params.projectSlug) qs.set("projectSlug", params.projectSlug);
  const query = qs.toString();
  const key = `${GENERATIONS_CACHE_PREFIX}${params.page ?? 1}:${
    params.perPage ?? ""
  }:${params.projectSlug ?? ""}:${params.locale ?? ""}`;
  return cachedRequest(
    key,
    () =>
      requestWithMeta<ApiGenerationHistoryItem[], ApiListMeta>(
        `/v1/generation${query ? `?${query}` : ""}`,
        {
          method: "GET",
          headers: {
            ...(params.locale ? { "Accept-Language": params.locale } : {}),
          },
        },
      ),
    LIST_TTL_MS,
  );
}

/** Dedupes concurrent GET /v1/generation/{slug} (e.g. React Strict Mode). */
const generationShowInflight = new Map<string, Promise<ApiGenerationHistoryItem>>();

/**
 * GET /v1/generation/{slug} — one history record.
 * JWT optional via cookie session: authenticated callers get own rows;
 * guests get guest rows by UUID slug.
 */
export function fetchGeneration(
  slug: string,
  params: FetchGenerationParams = {},
): Promise<ApiGenerationHistoryItem> {
  const path = `/v1/generation/${encodeURIComponent(slug)}`;
  const inflightKey = `${path}\0${params.locale ?? ""}`;

  const existing = generationShowInflight.get(inflightKey);
  if (existing) return existing;

  const promise = request<ApiGenerationHistoryItem>(path, {
    method: "GET",
    headers: {
      ...(params.locale ? { "Accept-Language": params.locale } : {}),
    },
  }).finally(() => {
    if (generationShowInflight.get(inflightKey) === promise) {
      generationShowInflight.delete(inflightKey);
    }
  });

  generationShowInflight.set(inflightKey, promise);
  return promise;
}

/**
 * PATCH /v1/generation/{slug} — update the user-facing title.
 * Authenticated callers only; own rows.
 */
export function updateGeneration(
  slug: string,
  body: GenerationUpdateBody,
  params: FetchGenerationParams = {},
): Promise<ApiGenerationHistoryItem> {
  return request<ApiGenerationHistoryItem>(
    `/v1/generation/${encodeURIComponent(slug)}`,
    {
      method: "PATCH",
      headers: {
        ...(params.locale ? { "Accept-Language": params.locale } : {}),
      },
      body: JSON.stringify(body),
    },
  ).then((updated) => {
    // The renamed title also appears on project cards.
    invalidateGenerationsAndProjects();
    return updated;
  });
}

function mapHistoryArtifacts(
  artifacts: ApiGenerationHistoryArtifact[] | undefined,
): ToolRunArtifact[] {
  return (artifacts ?? []).map((a) => ({
    role: a.role,
    url: a.url,
    fileName: a.fileName,
    mimeType: a.mimeType,
    extension: a.extension,
  }));
}

/** Map GET /v1/generation/{slug} onto the UI draft item shape. */
export function mapApiGenerationHistoryItem(
  g: ApiGenerationHistoryItem,
  locale?: string,
): ProjectItem {
  const createdMs = new Date(g.createdAt).getTime();
  return {
    name: generationItemTitle(g),
    slug: g.slug,
    tool: g.toolTitle,
    date: formatGenerationDate(g.createdAt, locale),
    generated: isGenerationToday(g.createdAt),
    createdAt: Number.isNaN(createdMs) ? undefined : createdMs,
    modality: castToolRunModality(g.modality),
    markdown: g.resultMarkdown ?? undefined,
    artifacts: mapHistoryArtifacts(g.artifacts),
  };
}
